import React from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  BarChart3,
  FileText,
  Settings,
  ChevronDown,
  Calendar,
  Network,
  Table2,
  Sparkles,
  LogOut,
  Users,
} from "lucide-react";
import { StructureTree } from "@/components/StructureTree";
import { AIAnalysis } from "@/components/AIAnalysis";
import { UsersDialog } from "@/components/UsersDialog";
import { useAuth } from "@/components/AuthContext";
import { ViewModeProvider, useViewMode } from "@/components/ViewModeContext";

const LOGO = process.env.PUBLIC_URL + "/logo-zae.png";
const LOGO_LIGHT = process.env.PUBLIC_URL + "/logo-zae-light.png";
const LOGO_CENTRAL = process.env.PUBLIC_URL + "/logo-central.png";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/" },
  { icon: Building2, label: "Estructura", to: "/" },
  { icon: BarChart3, label: "Indicadores", to: "/" },
  { icon: FileText, label: "Reportes", to: "/" },
];

function Sidebar({ onOpenStructure }) {
  const location = useLocation();
  return (
    <aside
      className="w-16 fixed left-0 top-0 bottom-0 bg-[#E2333F] flex flex-col items-center py-4 z-50"
      data-testid="sidebar"
    >
      <Link
        to="/"
        data-testid="sidebar-logo"
        className="mb-8 block"
      >
        <img src={LOGO} alt="ZAE Ejecutivo" className="w-10 h-10 rounded-md object-contain bg-black" />
      </Link>
      <nav className="flex flex-col items-center gap-2 flex-1">
        <Link
          to="/"
          title="Dashboard"
          data-testid="nav-dashboard"
          className={`w-10 h-10 flex items-center justify-center rounded-sm transition-colors ${
            location.pathname === "/" ? "bg-white/25" : "hover:bg-white/15"
          }`}
        >
          <LayoutDashboard className="w-5 h-5 text-white" strokeWidth={2} />
        </Link>
        <button
          onClick={onOpenStructure}
          title="Estructura"
          data-testid="nav-estructura"
          className="w-10 h-10 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"
        >
          <Network className="w-5 h-5 text-white" strokeWidth={2} />
        </button>
        {navItems.slice(2).map((item, i) => {
          const Icon = item.icon;
          return (
            <Link
              key={i}
              to={item.to}
              title={item.label}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="w-10 h-10 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"
            >
              <Icon className="w-5 h-5 text-white" strokeWidth={2} />
            </Link>
          );
        })}
      </nav>
      <button
        title="Configuración"
        data-testid="nav-settings"
        className="w-10 h-10 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"
      >
        <Settings className="w-5 h-5 text-white" strokeWidth={2} />
      </button>
    </aside>
  );
}

function ViewModeToggle() {
  const { showData, toggle } = useViewMode();
  return (
    <button
      onClick={toggle}
      data-testid="view-mode-toggle"
      title={showData ? "Mostrando datos · click para gráficas" : "Mostrando gráficas · click para datos"}
      className={`flex items-center gap-1.5 h-9 px-3 rounded-sm text-sm font-semibold transition-colors border ${
        showData
          ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-700"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      {showData ? <Table2 className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
      <span className="hidden lg:inline">{showData ? "Datos" : "Gráficas"}</span>
    </button>
  );
}

function UserMenu({ onOpenUsers }) {
  const { user, logout } = useAuth();
  const initials = (user?.name || user?.email || "US")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="flex items-center gap-2">
      {user?.role === "admin" && (
        <button
          onClick={onOpenUsers}
          data-testid="header-users-btn"
          title="Administrar usuarios"
          className="h-9 px-2.5 rounded-sm border border-slate-200 text-slate-600 hover:text-[#E2333F] hover:border-[#E2333F]/40 transition-colors flex items-center gap-1.5 text-sm font-semibold"
        >
          <Users className="w-4 h-4" />
          <span className="hidden lg:inline">Usuarios</span>
        </button>
      )}
      <div className="text-right hidden sm:block">
        <div className="text-[11px] text-slate-400 leading-none">Bienvenido</div>
        <div className="text-sm font-bold text-slate-800 leading-tight max-w-[140px] truncate">
          {user?.name || user?.email || "Usuario"}
        </div>
      </div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E2333F] to-[#9c1f29] flex items-center justify-center text-white font-display font-bold text-sm">
        {initials}
      </div>
      <button
        onClick={logout}
        data-testid="header-logout-btn"
        title="Cerrar sesión"
        className="w-9 h-9 flex items-center justify-center rounded-sm text-slate-400 hover:text-[#E2333F] hover:bg-slate-100 transition-colors"
      >
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
}

function Header({ onOpenStructure, onOpenAI, onOpenUsers }) {
  const navigate = useNavigate();
  return (
    <header className="h-16 ml-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-40">
      <div className="flex items-center gap-2.5">
        <button
          onClick={() => navigate("/")}
          data-testid="header-logo"
          className="flex items-center gap-2 select-none mr-1"
        >
          <img src={LOGO_CENTRAL} alt="ZAE Ejecutivo" className="h-9 w-9 rounded-md object-contain bg-black" />
          <span className="font-display font-light text-xl tracking-[0.16em] text-slate-800 hidden xl:inline">
            EJECUTIVO
          </span>
        </button>
        <div className="w-px h-7 bg-slate-200 mx-0.5 hidden md:block" />
        <button
          onClick={() => navigate("/")}
          data-testid="header-dashboard-btn"
          className="hidden lg:inline text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 hover:text-[#E2333F] transition-colors"
        >
          Dashboard
        </button>
        <button
          onClick={onOpenStructure}
          data-testid="header-estructura-btn"
          className="flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#E2333F] text-white text-sm font-semibold hover:bg-[#c92a36] transition-colors"
        >
          <Network className="w-4 h-4" />
          <span className="hidden sm:inline">Estructura</span>
        </button>
        <button
          onClick={onOpenAI}
          data-testid="header-ai-btn"
          className="flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1e293b] transition-colors"
        >
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="hidden md:inline">Análisis IA</span>
        </button>
        <ViewModeToggle />
        <div className="hidden xl:flex items-center gap-1.5 h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold text-slate-700 bg-white">
          <Calendar className="w-4 h-4 text-[#E2333F]" />
          Junio 2026
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <UserMenu onOpenUsers={onOpenUsers} />
      </div>
    </header>
  );
}

function deriveNode(pathname) {
  if (pathname.startsWith("/division/"))
    return { level: "division", id: decodeURIComponent(pathname.replace("/division/", "")) };
  if (pathname.startsWith("/zona/"))
    return { level: "zona", id: decodeURIComponent(pathname.replace("/zona/", "")) };
  if (pathname.startsWith("/regional/"))
    return { level: "regional", id: decodeURIComponent(pathname.replace("/regional/", "")) };
  if (pathname.startsWith("/company/"))
    return { level: "empresa", id: decodeURIComponent(pathname.replace("/company/", "")) };
  return { level: "grupo", id: null };
}

export default function Layout() {
  const [structureOpen, setStructureOpen] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [usersOpen, setUsersOpen] = React.useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const node = React.useMemo(() => deriveNode(location.pathname), [location.pathname]);
  return (
    <ViewModeProvider>
      <div className="App min-h-screen bg-[#f4f5f7]">
        <Sidebar onOpenStructure={() => setStructureOpen(true)} />
        <Header
          onOpenStructure={() => setStructureOpen(true)}
          onOpenAI={() => setAiOpen(true)}
          onOpenUsers={() => setUsersOpen(true)}
        />
        <main className="ml-16 p-6 min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
        <StructureTree open={structureOpen} onOpenChange={setStructureOpen} />
        <AIAnalysis open={aiOpen} onOpenChange={setAiOpen} node={node} />
        <UsersDialog open={usersOpen} onOpenChange={setUsersOpen} currentUserId={user?.id} />
      </div>
    </ViewModeProvider>
  );
}
